
import React, { useState } from 'react';
import { FileText, Calendar, Users, DollarSign, Hash, ChevronDown, ChevronUp } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';

interface DocumentSummaryDisplayProps {
  summary: string;
  documentType: string;
  keyData?: {
    names: string[];
    values: string[];
    dates: string[];
    processNumbers: string[];
    parties: string[];
  };
}

const DocumentSummaryDisplay: React.FC<DocumentSummaryDisplayProps> = ({
  summary,
  documentType,
  keyData
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Limit summary to 10 lines maximum
  const summaryLines = summary.split('\n').slice(0, 10);
  const displaySummary = summaryLines.join('\n');
  
  return (
    <div className="w-full max-w-4xl mx-auto px-4 mb-4">
      <Alert className="bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800">
        <FileText className="h-4 w-4 text-green-600 dark:text-green-400" />
        <AlertDescription className="text-green-800 dark:text-green-200">
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between p-0 h-auto text-left hover:bg-transparent"
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold">📄 Documento analisado com sucesso!</span>
                  <span className="text-xs bg-green-100 dark:bg-green-900 px-2 py-1 rounded-full">
                    {documentType.replace('_', ' ')}
                  </span>
                </div>
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-green-600 dark:text-green-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-green-600 dark:text-green-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="mt-3">
              <div className="space-y-3">
                <div className="border-t border-green-200 dark:border-green-800 pt-3">
                  <h4 className="font-medium mb-2 text-green-900 dark:text-green-100">
                    📋 RESUMO EXECUTIVO:
                  </h4>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap bg-white dark:bg-green-900/50 p-3 rounded-md border border-green-200 dark:border-green-700">
                    {displaySummary}
                  </div>
                </div>

                {keyData && (keyData.names.length > 0 || keyData.values.length > 0 || keyData.dates.length > 0 || keyData.processNumbers.length > 0) && (
                  <div className="border-t border-green-200 dark:border-green-800 pt-3">
                    <h4 className="font-medium mb-2 text-green-900 dark:text-green-100">
                      🔍 DADOS IDENTIFICADOS:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      {keyData.names.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Users className="h-3 w-3 mt-0.5 text-blue-600 dark:text-blue-400" />
                          <div>
                            <span className="font-medium">Pessoas:</span>
                            <div className="text-gray-600 dark:text-gray-300">
                              {keyData.names.slice(0, 3).join(', ')}
                              {keyData.names.length > 3 && ` (+${keyData.names.length - 3})`}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {keyData.values.length > 0 && (
                        <div className="flex items-start gap-2">
                          <DollarSign className="h-3 w-3 mt-0.5 text-green-600 dark:text-green-400" />
                          <div>
                            <span className="font-medium">Valores:</span>
                            <div className="text-gray-600 dark:text-gray-300">
                              {keyData.values.slice(0, 2).join(', ')}
                              {keyData.values.length > 2 && ` (+${keyData.values.length - 2})`}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {keyData.dates.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Calendar className="h-3 w-3 mt-0.5 text-purple-600 dark:text-purple-400" />
                          <div>
                            <span className="font-medium">Datas:</span>
                            <div className="text-gray-600 dark:text-gray-300">
                              {keyData.dates.slice(0, 2).join(', ')}
                              {keyData.dates.length > 2 && ` (+${keyData.dates.length - 2})`}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {keyData.processNumbers.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Hash className="h-3 w-3 mt-0.5 text-red-600 dark:text-red-400" />
                          <div>
                            <span className="font-medium">Processos:</span>
                            <div className="text-gray-600 dark:text-gray-300 font-mono text-xs">
                              {keyData.processNumbers[0]}
                              {keyData.processNumbers.length > 1 && ` (+${keyData.processNumbers.length - 1})`}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default DocumentSummaryDisplay;
